/**
 * ErrorBoundary - Phase 16 + Phase G Sentry Integration
 * Catches React errors and displays fallback UI
 */
import { Component, ErrorInfo, ReactNode } from 'react'
import { AlertTriangle, RefreshCw, Home } from 'lucide-react'
import './ErrorBoundary.css'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    }
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo })
    // Log error to monitoring service
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    // Report to our error logger
    import('../services/errorLogger').then(({ errorLogger }) => {
      errorLogger.captureReactError(error, { componentStack: errorInfo.componentStack || '' })
    }).catch(() => {
      // Error logger not available, ignore
    })
    // Report to Sentry if initialized - lazy load to reduce bundle size
    import('@sentry/react').then((Sentry) => {
      Sentry.captureException(error, { extra: { componentStack: errorInfo.componentStack } })
    }).catch(() => {
      // Sentry not available, ignore
    })
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null })
  }

  handleGoHome = (): void => {
    window.location.href = '/'
  }

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="error-boundary">
          <div className="error-boundary-content">
            <div className="error-boundary-icon">
              <AlertTriangle size={48} />
            </div>
            <h1 className="error-boundary-title">Something went wrong</h1>
            <p className="error-boundary-message">
              We apologize for the inconvenience. An unexpected error has occurred.
            </p>
            {this.state.error && (
              <div className="error-boundary-details">
                <code>{this.state.error.message}</code>
              </div>
            )}
            <div className="error-boundary-actions">
              <button
                className="error-boundary-btn primary"
                onClick={this.handleRetry}
              >
                <RefreshCw size={16} />
                Try Again
              </button>
              <button
                className="error-boundary-btn secondary"
                onClick={this.handleGoHome}
              >
                <Home size={16} />
                Go to Dashboard
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

// Route-level error boundary with page-specific fallback
export function RouteErrorBoundary({ children }: { children: ReactNode }) {
  return (
    <ErrorBoundary
      fallback={
        <div className="route-error">
          <AlertTriangle size={32} />
          <h2>Page Error</h2>
          <p>This page encountered an error. Try refreshing.</p>
          <button onClick={() => window.location.reload()}>
            <RefreshCw size={14} />
            Refresh Page
          </button>
        </div>
      }
    >
      {children}
    </ErrorBoundary>
  )
}
